import { useCallback } from "react";
import { useParams } from "react-router-dom";
import { useApi } from "../api/context";
import type { Prospect } from "../api/types";
import { DemoFlow } from "../demo/DemoFlow";
import { DemoShell } from "../demo/DemoShell";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import { useLoader } from "../lib/useLoader";

export function LinkDemoPage() {
  useDocumentTitle("Your live demo");
  const { token = "" } = useParams();
  const api = useApi();
  const loadInvitation = useCallback(() => api.describeLink(token), [api, token]);
  const invitation = useLoader(loadInvitation);
  const start = useCallback((prospect: Prospect) => api.startLinkSession(token, prospect), [api, token]);

  let content;
  if (invitation.error) {
    content = (
      <section className="demo-card">
        <h1 className="demo-title">This link isn't available</h1>
        <p className="muted">{invitation.error.message}</p>
      </section>
    );
  } else if (!invitation.data) {
    content = (
      <section className="demo-card row" role="status">
        <span className="spinner" aria-hidden="true" /> Checking your invitation…
      </section>
    );
  } else if (!invitation.data.ready) {
    content = (
      <section className="demo-card">
        <h1 className="demo-title">This demo isn't live yet</h1>
        <p className="muted">
          {invitation.data.tenant_name} is still getting their demo ready. Please try your link again a little later.
        </p>
      </section>
    );
  } else {
    content = (
      <DemoFlow
        heading={`${invitation.data.tenant_name} invited you to a live demo`}
        intro="An AI guide will ask what you're working on, show you the parts of the product that matter to you, and can book a call with the team."
        initialProspect={{ name: invitation.data.recipient_name ?? "", email: invitation.data.recipient_email }}
        onStart={start}
      />
    );
  }

  return <DemoShell embedded={false}>{content}</DemoShell>;
}
